/* This example requires Tailwind CSS v2.0+ */
import React, { useContext, useEffect, useState } from "react";

import { OrgContext } from "contexts/OrgContext";
import { Redirect, useHistory, useLocation, useParams } from "react-router-dom";
import { UserContext } from "contexts/UserContext";
import { UserPlusIcon } from "@heroicons/react/24/solid";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "components/Graphs/Tabs";
import { Button } from "components/Graphs/Button";
import TeamInvite from "./TeamInvite";
import TeamInvitations from "./TeamInvitations";
import { InviteUserDialog } from "./InviteUserDialog";
import TeamMembers from "./TeamMembers";
import { isOrgAdminRole } from "utils/roleUtils";
import { usesNcaUserManagement } from "./userManagement";
import ApplicationPageLayout from "components/ApplicationShell/ApplicationPageLayout";
import {
  isApplicationShellRedesigned,
  useApplicationShellPreference,
} from "components/ApplicationShell/ApplicationShellPreference";
import { getOrganizationSettingsTabRedirectHref } from "components/OrgSettings/settingsTabs";

export const OrgTeamRedirect: React.FC = () => {
  const orgContext = useContext(OrgContext);
  const history = useHistory();
  const location = useLocation();
  const shellPreference = useApplicationShellPreference();
  const useOrganizationSettingsTabs =
    isApplicationShellRedesigned(shellPreference);

  useEffect(() => {
    if (orgContext.activeOrgId) {
      if (useOrganizationSettingsTabs) {
        const redirectHref = getOrganizationSettingsTabRedirectHref({
          activeOrgId: orgContext.activeOrgId,
          tab: "team",
          search: location.search,
        });

        if (redirectHref) history.replace(redirectHref);
      } else {
        history.push(`/org/${orgContext.activeOrgId}/team`);
      }
    }
  }, [
    history,
    location.search,
    orgContext.activeOrgId,
    useOrganizationSettingsTabs,
  ]);
  return <></>;
};

interface OrgPagePeopleProps {
  embedded?: boolean;
}

const OrgPagePeople: React.FC<OrgPagePeopleProps> = ({ embedded = false }) => {
  const orgContext = useContext(OrgContext);
  const userContext = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();
  const { orgId } = useParams<{ orgId?: string }>();
  const shellPreference = useApplicationShellPreference();
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const activeOrg = orgContext.activeOrg;
  const useLegacyInviteLinkFlow = !usesNcaUserManagement(activeOrg);
  const canManageTeam = isOrgAdminRole(orgContext.activeOrgRole);
  const useOrganizationSettingsTabs =
    isApplicationShellRedesigned(shellPreference);
  const canInviteUsers =
    canManageTeam &&
    !useLegacyInviteLinkFlow &&
    activeOrg?.isBlocked !== true &&
    userContext.me?.isBlocked !== true;

  useEffect(() => {
    document.title = "Team | Brev.dev";
  }, []);

  useEffect(() => {
    if (
      !useOrganizationSettingsTabs &&
      !orgContext.isLoading &&
      !orgContext.isOrgAccessLoading &&
      !orgContext.isOrgAccessError &&
      userContext.me &&
      orgContext.activeOrgId &&
      !canManageTeam
    ) {
      history.replace(`/org/${orgContext.activeOrgId}/environments`);
    }
  }, [
    canManageTeam,
    history,
    orgContext.activeOrgId,
    orgContext.isOrgAccessError,
    orgContext.isOrgAccessLoading,
    orgContext.isLoading,
    useOrganizationSettingsTabs,
    userContext.me,
  ]);

  if (useOrganizationSettingsTabs && !embedded) {
    const redirectHref = getOrganizationSettingsTabRedirectHref({
      activeOrgId: orgContext.activeOrgId,
      routeOrgId: orgId,
      tab: "team",
      search: location.search,
    });

    if (redirectHref) {
      return <Redirect to={redirectHref} />;
    }
  }

  if (
    orgContext.isOrgAccessLoading ||
    orgContext.isOrgAccessError ||
    !canManageTeam
  ) {
    return <></>;
  }

  const content = (
    <div className={embedded ? "" : "px-4 sm:px-6 lg:px-8"}>
      {!embedded && (
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Team
            </h1>
            <div className="flex">
              <p className="mt-2 text-sm text-gray-700 dark:text-secondary">
                Manage your team members, permissions, and invitations
              </p>
            </div>
          </div>
        </div>
      )}
      <div className={embedded ? "flex flex-col" : "mt-5 flex flex-col"}>
        <div
          className={
            embedded
              ? "overflow-y-visible"
              : "-my-2 -mx-4 overflow-y-visible sm:-mx-6 lg:-mx-8"
          }
        >
          <div
            className={
              embedded
                ? "min-w-full align-middle"
                : "min-w-full py-2 align-middle md:px-6 lg:px-8"
            }
          >
            {!useLegacyInviteLinkFlow && canManageTeam ? (
              <Tabs defaultValue="members">
                <div className="flex flex-col gap-3 border-b border-gray-200 dark:border-zinc-800 sm:flex-row sm:items-end sm:justify-between">
                  <TabsList className="border-b-0">
                    <TabsTrigger value="members">Team Members</TabsTrigger>
                    <TabsTrigger value="invitations">
                      User Invitations
                    </TabsTrigger>
                  </TabsList>
                  {canInviteUsers && (
                    <Button
                      variant="primary"
                      className="mb-3 self-start sm:self-auto"
                      onClick={() => setIsInviteDialogOpen(true)}
                    >
                      <UserPlusIcon className="mr-2 h-4 w-4" />
                      Invite user
                    </Button>
                  )}
                </div>
                {canInviteUsers && (
                  <InviteUserDialog
                    open={isInviteDialogOpen}
                    onOpenChange={setIsInviteDialogOpen}
                    organizationId={orgContext.activeOrgId}
                  />
                )}
                <TabsContent value="members" className="mt-5">
                  <TeamMembers showActions={canManageTeam} />
                </TabsContent>
                <TabsContent value="invitations" className="mt-5">
                  <TeamInvitations organizationId={orgContext.activeOrgId} />
                </TabsContent>
              </Tabs>
            ) : (
              <>
                {canManageTeam && (
                  <div className="flex flex-row justify-center items-center mb-5">
                    <TeamInvite />
                  </div>
                )}
                <TeamMembers showActions={canManageTeam} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <ApplicationPageLayout className="py-10 sm:px-6 lg:px-8">
      {content}
    </ApplicationPageLayout>
  );
};

export default OrgPagePeople;
